import React, { useCallback, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import { Location } from 'history'

import TextButton from 'components/Luxkit/Button/TextButton'
import Dropdown from 'components/Luxkit/Dropdown'
import RadioButtonInput from 'components/Luxkit/RadioButton/RadioButtonInput'
import useToggle from 'hooks/useToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Breakpoint from 'components/Common/Breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { OfferListSortOption, SORT_OPTION_RECOMMENDED } from 'constants/offerListFilters'
import { parseSearchString } from 'lib/urlUtils'
import { replace } from 'connected-react-router'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import Modal from 'components/Luxkit/Modal/Modal'

const SortMenuDesktop = styled(Dropdown)`
  display: none;
  min-width: ${rem(220)};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  & > label + label {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.sm} {
    display: block;
  }
`

const OptionsWrapper = styled.div`
  position: relative;
`
interface Props {
  sortOptions: Array<OfferListSortOption>;
  windowSearch: string;
  condenseOnMobile?: boolean;
  replace: Utils.ActionDispatcher<typeof replace>,
  onOpen?: () => void;
  onSelect?: (option: OfferListSortOption) => void;
  location: Location
}

function OfferSortControl(props: Props) {
  const { sortOptions, windowSearch, condenseOnMobile, replace, onOpen, onSelect, location } = props
  const { value: isOpenSort, toggle: toggleSort, off: closeSort } = useToggle()

  useEffect(() => {
    isOpenSort && onOpen?.()
  }, [isOpenSort, onOpen])

  const searchParams = parseSearchString(windowSearch)

  const currentOption = sortOptions.find(option => option.value === searchParams.sortBy) ?? SORT_OPTION_RECOMMENDED

  useEffect(() => {
    // If we somehow end up with an invalid sort option, remove it
    if (
      searchParams.sortBy &&
      searchParams.sortBy !== 'sales' &&
      searchParams.sortBy !== 'endingsoon' &&
      !sortOptions.some(option => option.value === searchParams.sortBy)
    ) {
      const newParams = new URLSearchParams(windowSearch)
      newParams.delete('sortBy')
      // keep the rest of the url the same besides removing the sortBy
      replace({ ...location, search: newParams.toString() })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replace, searchParams.sortBy, sortOptions, windowSearch])

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const value = e.currentTarget.value
    const newParams = new URLSearchParams(windowSearch)
    if (value === 'recommended') {
      // `recommended` is default. In this case, leave the param out entirely for consistency
      newParams.delete('sortBy')
    } else {
      newParams.set('sortBy', value)
    }
    replace({ search: newParams.toString() })
    closeSort()
    onSelect?.(sortOptions.find(option => option.value === value))
    window.scrollTo({ behavior: 'instant' as any, top: 0 })
  }, [windowSearch, replace, closeSort, onSelect, sortOptions])

  const sortInputs = sortOptions.map(option => <RadioButtonInput
    key={option.value}
    checked={currentOption.value === option.value}
    onChange={onChange}
    name={option.label}
    value={option.value}
  >
    {option.label}
  </RadioButtonInput>)

  const triggerRef = useRef<HTMLButtonElement>()

  return (
    <OptionsWrapper>
      <TextButton kind="secondary" variant="dark" onClick={toggleSort} endIcon={<DropdownChevron/>} ref={triggerRef}>
        {condenseOnMobile && <>
          <CSSBreakpoint max="xxs" as="span">Sort</CSSBreakpoint>
          <CSSBreakpoint min="xs" as="span">Sort: {currentOption.label}</CSSBreakpoint>
        </>}
        {!condenseOnMobile && <>
          Sort: {currentOption.label}
        </>}
      </TextButton>
      <SortMenuDesktop open={isOpenSort} onClose={closeSort} triggerRef={triggerRef}>
        {sortInputs}
      </SortMenuDesktop>
      <Breakpoint max="xs">
        <Modal title="Sort by" onClose={closeSort} isOpen={isOpenSort}>
          {sortInputs}
        </Modal>
      </Breakpoint>
    </OptionsWrapper>
  )
}

const mapStateToProps = (state: App.State) => ({
  windowSearch: state.router.location.search,
  location: state.router.location,
})

const mapDispatchToProps = {
  replace,
}

export default connect(mapStateToProps, mapDispatchToProps)(OfferSortControl)
